/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { getPublicSiteBasePath, stripBasePath, withBasePath } from "@cosmos/common-utils";
import { isError } from "@epic-mw/error-types";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

export interface UseAccountStateOptions {
	/**
	 * Site base path (e.g. "/site" for epicgames, "/o" for fab). Defaults to
	 * `NEXT_PUBLIC_COSMOS_SITE_BASE_PATH`, which `createEpicNextConfig` sets from
	 * `COSMOS_SITE_BASE_PATH`. Pass it explicitly in the unified cosmos-website,
	 * where a single build serves tenants with different base paths.
	 */
	basePath?: string;
	/**
	 * Current locale. Only an effect dependency, so sites that carry the locale in
	 * `?lang=` rather than the pathname rebuild the state when it changes.
	 */
	locale?: string;
}

/**
 * Prefixes `pathname` with `basePath`, collapsing any prefix already present.
 *
 * Next strips its configured `basePath` from `usePathname()`, so standalone sites
 * served under a sub-path (epicgames at `/site`, fab at `/o`) have to add it back
 * or the account portal returns to the wrong URL. The unified cosmos-website has
 * no Next `basePath`, so whether the prefix is already present depends on the
 * tenant's edge router.
 *
 * Delegates to `withBasePath`, which is the one place this join is implemented.
 *
 * @deprecated Use `withBasePath` from `@cosmos/common-utils` directly.
 */
export const withSiteBasePath = (pathname: string, basePath: string): string => withBasePath(pathname, basePath);

/**
 * Builds the search string for the account sign-in/out links: the current URL's
 * search params plus a `state` param holding the base-path-qualified path the
 * account portal should return to.
 *
 * `state` is deliberately base-path-RELATIVE. The server composes the return
 * URL as `getSiteOrigin() + state`, and `getSiteOrigin()` already appends
 * `COSMOS_SITE_BASE_PATH` (tokenUtils.ts). So sending `/site/news` produced
 * `https://www.epicgames.com/site/site/news`. Send `/news` and the server makes
 * it `https://www.epicgames.com/site/news`.
 *
 * Stripping rather than passing `usePathname()` straight through, because the
 * two app shapes disagree about what it contains: Next removes its configured
 * `basePath` on the standalone sites, so it is already relative there, while the
 * unified cosmos-website has no Next `basePath` and sees the tenant prefix in
 * the path. `stripBasePath` is a no-op in the first case and does the work in
 * the second.
 *
 * @param href - Current document href (`window.location.href`)
 * @param pathname - Pathname from `usePathname()`
 * @param basePath - Site base path (e.g. "/o"); empty string means none
 * @returns The encoded search string, or `null` when `href` can't be parsed
 *
 * @example
 * buildAccountState("https://www.epicgames.com/site/news?mode=game", "/news", "/site")
 * // => "mode=game&state=%2Fnews"
 */
export const buildAccountState = (href: string, pathname: string, basePath = ""): string | null => {
	try {
		const url = new URL(href);
		url.searchParams.set("state", stripBasePath(pathname, basePath).routePath);
		return url.searchParams.toString();
	} catch (e) {
		console.log("Failed to parse href building accountState", href, isError(e) && e.message);
		return null;
	}
};

/**
 * Search string for the account sign-in/out links, kept in sync with the current
 * route. Empty until the first client render, so it is safe in SSR.
 *
 * Prefer {@link useAccountLinks} for the links themselves: the href needs the
 * base path too, and building it by hand is what caused MW-13701.
 *
 * @example
 * const accountState = useAccountState({ locale });
 */
export const useAccountState = ({ basePath, locale }: UseAccountStateOptions = {}): string => {
	const pathname = usePathname();
	const [accountState, setAccountState] = useState("");
	const siteBasePath = getPublicSiteBasePath(basePath);

	useEffect(() => {
		if (!window.location.href) return;

		const nextState = buildAccountState(window.location.href, pathname, siteBasePath);
		// Keep the previous value when the href can't be parsed.
		if (nextState !== null) setAccountState(nextState);
	}, [pathname, locale, siteBasePath]);

	return accountState;
};

export interface AccountLinks {
	signInLinkHref: string;
	signOutLinkHref: string;
}

/**
 * The account portal sign-in and sign-out hrefs for the current route, including
 * the site base path.
 *
 * MW-13701: these used to be written inline as `` `/logout${...}` `` at each call
 * site, which drops the base path. Next only rewrites `<Link>` and router
 * navigations with the configured `basePath`, not plain strings handed to the
 * navigation web component, so on a sub-path site the browser navigated to
 * `/logout` instead of `/site/logout`. That is not a cosmos route, so the edge
 * router served it from the legacy site: legacy cleared the cookies it owns and
 * left cosmos's `EPIC_EG1` and `EPIC_EG1_REFRESH` behind, an orphaned session
 * with nothing able to re-mint it. The user was then signed out of cosmos with
 * no route back.
 *
 * The base path resolves exactly as it does for the `state` param, so the href
 * and the return URL can no longer disagree.
 *
 * @example
 * const { signInLinkHref, signOutLinkHref } = useAccountLinks({ locale });
 */
export const useAccountLinks = ({ basePath, locale }: UseAccountStateOptions = {}): AccountLinks => {
	const accountState = useAccountState({ basePath, locale });
	const siteBasePath = getPublicSiteBasePath(basePath);
	const search = accountState ? `?${accountState}` : "";

	/*
	  Through `withBasePath` rather than a template concat, so the href cannot
	  double either. The same base path reaches these links from the env var here
	  and from the Hygraph `hrefTemplate` (`{domain}/site/login`), which legacy
	  needs, and a concat of two sources that both carry it yields `/site/site`.
	*/
	return {
		signInLinkHref: `${withBasePath("/login", siteBasePath)}${search}`,
		signOutLinkHref: `${withBasePath("/logout", siteBasePath)}${search}`,
	};
};
